// SPDX-FileCopyrightText: 2026 LiveKit, Inc.
//
// SPDX-License-Identifier: Apache-2.0
import { RoomServiceClient } from 'livekit-server-sdk';
import { afterEach, beforeAll, describe, expect, it, vi } from 'vitest';
import { APIStatusError, APITimeoutError } from '../_exceptions.js';
import { runWithJobContextAsync } from '../job.js';
import { initializeLogger } from '../log.js';
import { type APIConnectOptions } from '../types.js';
import type * as AvatarIndex from '../voice/avatar/index.js';
import { AvatarSession, parseAvatarModel } from './avatar.js';
import type * as InferenceUtils from './utils.js';
import { INFERENCE_PROVIDER_HEADER } from './utils.js';

const { fakeSinks, mintAccessToken } = vi.hoisted(() => {
  let tokenSequence = 0;
  return {
    fakeSinks: [] as Array<{ sampleRate?: number; destinationIdentity: string }>,
    mintAccessToken: vi.fn(async () => `gateway-token-${++tokenSequence}`),
  };
});

vi.mock('../voice/avatar/index.js', async (importOriginal) => {
  const actual = await importOriginal<typeof AvatarIndex>();
  return {
    ...actual,
    DataStreamAudioOutput: class FakeDataStreamAudioOutput {
      readonly sampleRate?: number;
      readonly destinationIdentity: string;

      constructor(opts: { sampleRate?: number; destinationIdentity: string }) {
        this.sampleRate = opts.sampleRate;
        this.destinationIdentity = opts.destinationIdentity;
        fakeSinks.push(this);
      }
    },
  };
});

vi.mock('./utils.js', async (importOriginal) => {
  const actual = await importOriginal<typeof InferenceUtils>();
  return { ...actual, createAccessToken: mintAccessToken };
});

beforeAll(() => {
  initializeLogger({ level: 'silent', pretty: false });
});

afterEach(() => {
  vi.unstubAllEnvs();
});

function makeAvatar(overrides: Partial<ConstructorParameters<typeof AvatarSession>[0]> = {}) {
  return new AvatarSession({
    model: 'lemonslice',
    apiKey: 'test-key',
    apiSecret: 'test-secret',
    baseURL: 'https://example.livekit.cloud/v1',
    ...overrides,
  });
}

function jsonResponse(body: unknown, init: ResponseInit = {}): Response {
  return new Response(JSON.stringify(body), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  });
}

async function callCreate(av: AvatarSession) {
  return await av._createSession({
    roomName: 'my-room',
    roomSid: 'RM_123',
    livekitUrl: 'wss://example.livekit.cloud',
    agentIdentity: 'agent-worker-1',
  });
}

class FakeOutput {
  audio: unknown = null;
}

class FakeAgentSession {
  output = new FakeOutput();
  _started = false;
  on() {}
  off() {}
  emit() {}
}

class FakeRoom {
  name = 'my-room';
  isConnected = false;
  localParticipant?: { identity: string };
  // Mirrors `@livekit/rtc-node` `Room.getSid()` (there is no public `sid` property);
  // an unconnected room resolves to an empty string.
  async getSid(): Promise<string> {
    return '';
  }
  on() {}
  off() {}
}

class FakeConnectedRoom extends FakeRoom {
  override isConnected = true;
  override localParticipant = { identity: 'standalone-agent' };
  override async getSid(): Promise<string> {
    return 'RM_789';
  }
}

class FakeJobRoom extends FakeRoom {
  // `@livekit/rtc-node` reports an empty string (not undefined) before the room connects.
  override name = '';
}

describe('parseAvatarModel', () => {
  it.each([
    ['lemonslice', 'lemonslice', undefined],
    ['lemonslice/agent_abc', 'lemonslice', 'agent_abc'],
    ['lemonslice/', 'lemonslice', undefined],
    ['bey/face_1', 'bey', 'face_1'],
  ])('parses %s', (model, provider, avatarId) => {
    expect(parseAvatarModel(model)).toEqual([provider, avatarId]);
  });

  it.each(['', '/foo', '  '])('rejects invalid provider %s', (model) => {
    expect(() => parseAvatarModel(model)).toThrow();
  });
});

describe('AvatarSession constructor', () => {
  it('sets defaults and identity', () => {
    const av = makeAvatar();
    expect(av.provider).toBe('lemonslice');
    expect(av.avatarIdentity).toBe('lemonslice-inference-avatar');
  });

  it('uses LIVEKIT_API_KEY fallback credentials', () => {
    vi.stubEnv('LIVEKIT_INFERENCE_API_KEY', undefined);
    vi.stubEnv('LIVEKIT_INFERENCE_API_SECRET', undefined);
    vi.stubEnv('LIVEKIT_API_KEY', 'env-key');
    vi.stubEnv('LIVEKIT_API_SECRET', 'env-secret');

    const av = new AvatarSession({ model: 'lemonslice', baseURL: 'https://x/v1' });
    expect(av['apiKey']).toBe('env-key');
    expect(av['apiSecret']).toBe('env-secret');
  });

  it('rejects missing credentials', () => {
    vi.stubEnv('LIVEKIT_API_KEY', undefined);
    vi.stubEnv('LIVEKIT_API_SECRET', undefined);
    vi.stubEnv('LIVEKIT_INFERENCE_API_KEY', undefined);
    vi.stubEnv('LIVEKIT_INFERENCE_API_SECRET', undefined);

    expect(() => new AvatarSession({ model: 'lemonslice', baseURL: 'https://x/v1' })).toThrow();
  });

  it('supports custom identity', () => {
    expect(makeAvatar({ avatarParticipantIdentity: 'custom-id' }).avatarIdentity).toBe('custom-id');
  });

  it('rejects image_url with model id', () => {
    expect(() =>
      makeAvatar({ model: 'lemonslice/agent_abc', extraKwargs: { image_url: 'https://x/y.png' } }),
    ).toThrow(/not both/);
  });

  it('copies extraKwargs defensively', () => {
    const extra = { prompt: 'hi' };
    const av = makeAvatar({ extraKwargs: extra });
    extra.prompt = 'changed';
    expect(av['extraKwargs']).toEqual({ prompt: 'hi' });
  });
});

it('splits known LemonSlice options and unknown extra kwargs', async () => {
  let captured: Record<string, unknown> | undefined;
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    captured = JSON.parse(String(init?.body)) as Record<string, unknown>;
    return jsonResponse({ session_id: 'AVS_1' });
  });

  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    extraKwargs: { idle_prompt: 'look attentive', some_future_knob: true },
  });
  await callCreate(av);

  expect(captured?.idle_prompt).toBe('look attentive');
  expect(captured?.extra_kwargs).toEqual({ some_future_knob: true });
});

it('creates a gateway session with payload, headers, and idempotency key', async () => {
  let capturedHeaders: Headers | undefined;
  let capturedBody: Record<string, unknown> | undefined;
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    capturedHeaders = new Headers(init?.headers);
    capturedBody = JSON.parse(String(init?.body)) as Record<string, unknown>;
    return jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_abc', sample_rate: 16000 });
  });

  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    extraKwargs: {
      image_url: 'https://example.com/face.png',
      prompt: 'be expressive',
      idle_timeout: 300,
    },
  });
  const resp = await callCreate(av);

  expect(resp.session_id).toBe('AVS_1');
  expect(resp.provider_session_id).toBe('ls_abc');
  expect(capturedBody).toMatchObject({
    provider: 'lemonslice',
    avatar_identity: 'lemonslice-inference-avatar',
    avatar_name: 'lemonslice-inference-avatar',
    agent_identity: 'agent-worker-1',
    room_name: 'my-room',
    room_sid: 'RM_123',
    image_url: 'https://example.com/face.png',
    prompt: 'be expressive',
    idle_timeout_s: 300,
  });
  expect(capturedBody).not.toHaveProperty('livekit_token');
  expect(capturedBody).not.toHaveProperty('extra_kwargs');
  expect(capturedHeaders?.get('Authorization')).toMatch(/^Bearer /);
  expect(capturedHeaders?.get(INFERENCE_PROVIDER_HEADER)).toBe('lemonslice');
  expect(capturedHeaders?.get('Idempotency-Key')).toBeTruthy();
});

it('sends avatar_id from model string', async () => {
  let captured: Record<string, unknown> | undefined;
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    captured = JSON.parse(String(init?.body)) as Record<string, unknown>;
    return jsonResponse({ session_id: 'AVS_1' });
  });

  const av = makeAvatar({ model: 'lemonslice/agent_abc', fetch: fetchMock as typeof fetch });
  await callCreate(av);

  expect(captured?.avatar_id).toBe('agent_abc');
  expect(captured).not.toHaveProperty('image_url');
});

it('refreshes authorization while keeping the idempotency key stable across retries', async () => {
  const authorizations: string[] = [];
  const keys: string[] = [];
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    const headers = new Headers(init?.headers);
    authorizations.push(headers.get('Authorization') ?? '');
    keys.push(headers.get('Idempotency-Key') ?? '');
    if (keys.length < 3) return jsonResponse({ error: 'unavailable' }, { status: 503 });
    return jsonResponse({ session_id: 'AVS_1' });
  });

  const connOptions: APIConnectOptions = { maxRetry: 3, retryIntervalMs: 0, timeoutMs: 5 };
  const av = makeAvatar({ fetch: fetchMock as typeof fetch, connOptions });
  const resp = await callCreate(av);

  expect(resp.session_id).toBe('AVS_1');
  expect(keys).toHaveLength(3);
  expect(new Set(authorizations).size).toBe(3);
  expect(new Set(keys).size).toBe(1);
});

it('does not retry non-retryable errors', async () => {
  const fetchMock = vi.fn(async () => jsonResponse({ error: 'not enabled' }, { status: 403 }));
  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    connOptions: { maxRetry: 3, retryIntervalMs: 0, timeoutMs: 5 },
  });

  await expect(callCreate(av)).rejects.toMatchObject({ statusCode: 403 });
  expect(fetchMock).toHaveBeenCalledTimes(1);
});

it('retries server errors then raises', async () => {
  const fetchMock = vi.fn(async () => jsonResponse({ error: 'boom' }, { status: 502 }));
  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    connOptions: { maxRetry: 2, retryIntervalMs: 0, timeoutMs: 5 },
  });

  await expect(callCreate(av)).rejects.toBeInstanceOf(APIStatusError);
  expect(fetchMock).toHaveBeenCalledTimes(3);
});

it('honors the gateway Retry-After hint over the default backoff interval', async () => {
  vi.useFakeTimers();
  try {
    let calls = 0;
    const fetchMock = vi.fn(async () => {
      calls += 1;
      if (calls === 1) {
        return jsonResponse(
          { error: 'rate limited' },
          { status: 429, headers: { 'Retry-After': '30' } },
        );
      }
      return jsonResponse({ session_id: 'AVS_1' });
    });
    // Default backoff would retry after ~2s; the gateway asked for 30s.
    const av = makeAvatar({
      fetch: fetchMock as typeof fetch,
      connOptions: { maxRetry: 1, retryIntervalMs: 2000, timeoutMs: 60000 },
    });

    const promise = callCreate(av);
    await vi.advanceTimersByTimeAsync(0);
    expect(fetchMock).toHaveBeenCalledTimes(1);

    // The default 2s interval must NOT trigger the retry.
    await vi.advanceTimersByTimeAsync(2000);
    expect(fetchMock).toHaveBeenCalledTimes(1);

    // Once the 30s Retry-After elapses, the retry fires and succeeds.
    await vi.advanceTimersByTimeAsync(28000);
    const resp = await promise;
    expect(resp.session_id).toBe('AVS_1');
    expect(fetchMock).toHaveBeenCalledTimes(2);
  } finally {
    vi.useRealTimers();
  }
});

it('aborts gateway requests using the configured connOptions timeout', async () => {
  // A fetch that never resolves on its own, so the only way this settles is the
  // request timeout signal. With the old hard-coded 60s the test would hang; the
  // small connOptions.timeoutMs proves the caller-provided timeout is honored.
  const fetchMock = vi.fn(
    (_url: string | URL | Request, init?: RequestInit) =>
      new Promise<Response>((_resolve, reject) => {
        init?.signal?.addEventListener('abort', () => reject((init.signal as AbortSignal).reason));
      }),
  );
  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    connOptions: { maxRetry: 0, retryIntervalMs: 0, timeoutMs: 10 },
  });

  await expect(callCreate(av)).rejects.toBeInstanceOf(APITimeoutError);
});

it('start uses response sample rate and captures terminate token', async () => {
  fakeSinks.length = 0;
  const fetchMock = vi.fn(async () =>
    jsonResponse({
      session_id: 'AVS_1',
      provider_session_id: 'ls_1',
      terminate_token: 'tt_1',
      sample_rate: 24000,
    }),
  );
  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  const agentSession = new FakeAgentSession();
  await av.start(agentSession as never, new FakeConnectedRoom() as never, {
    livekitUrl: 'wss://example.livekit.cloud',
  });

  expect(av.sessionId).toBe('AVS_1');
  expect(av.providerSessionId).toBe('ls_1');
  expect(av['_terminateToken']).toBe('tt_1');
  expect(fakeSinks[0]?.sampleRate).toBe(24000);
  expect(agentSession.output.audio).toBe(fakeSinks[0]);
});

it('start sends mint inputs and no token', async () => {
  let captured: Record<string, unknown> | undefined;
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    captured = JSON.parse(String(init?.body)) as Record<string, unknown>;
    return jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1' });
  });

  const av = makeAvatar({ fetch: fetchMock as typeof fetch, avatarParticipantName: 'Ada' });
  await av.start(new FakeAgentSession() as never, new FakeConnectedRoom() as never, {
    livekitUrl: 'wss://example.livekit.cloud',
  });

  expect(captured).not.toHaveProperty('livekit_token');
  expect(captured?.room_name).toBe('my-room');
  expect(captured?.avatar_identity).toBe('lemonslice-inference-avatar');
  expect(captured?.avatar_name).toBe('Ada');
  expect(captured?.agent_identity).toBe('standalone-agent');
  expect(captured?.room_sid).toBe('RM_789');
});

it('start warns when the gateway minted a different avatar identity', async () => {
  const fetchMock = vi.fn(async () =>
    jsonResponse({
      session_id: 'AVS_1',
      provider_session_id: 'ls_1',
      avatar_identity: 'gateway-normalized-avatar',
    }),
  );

  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  // The logger is the process-wide `log()` singleton, so drop calls made by earlier tests.
  const warn = vi.spyOn(av['logger'], 'warn').mockClear();
  await av.start(new FakeAgentSession() as never, new FakeConnectedRoom() as never, {
    livekitUrl: 'wss://example.livekit.cloud',
  });

  expect(warn).toHaveBeenCalledWith(
    expect.objectContaining({
      'lk.pii.requested_identity': 'lemonslice-inference-avatar',
      'lk.pii.minted_identity': 'gateway-normalized-avatar',
    }),
    expect.stringContaining('different identity than requested'),
  );
});

it.each([
  ['echoes the requested identity', 'lemonslice-inference-avatar'],
  ['omits avatar_identity', undefined],
])('start does not warn when the gateway %s', async (_label, avatarIdentity) => {
  const fetchMock = vi.fn(async () =>
    jsonResponse({
      session_id: 'AVS_1',
      provider_session_id: 'ls_1',
      avatar_identity: avatarIdentity,
    }),
  );

  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  // The logger is the process-wide `log()` singleton, so drop calls made by earlier tests.
  const warn = vi.spyOn(av['logger'], 'warn').mockClear();
  await av.start(new FakeAgentSession() as never, new FakeConnectedRoom() as never, {
    livekitUrl: 'wss://example.livekit.cloud',
  });

  // Scoped to the mismatch message: the shared logger also carries unrelated warnings.
  expect(warn).not.toHaveBeenCalledWith(
    expect.anything(),
    expect.stringContaining('different identity than requested'),
  );
});

it('start without livekitUrl raises', async () => {
  vi.stubEnv('LIVEKIT_URL', undefined);

  await expect(
    makeAvatar().start(new FakeAgentSession() as never, new FakeConnectedRoom() as never),
  ).rejects.toThrow(/livekitUrl/);
});

it('start uses job room name and sid before the rtc room is connected', async () => {
  let captured: Record<string, unknown> | undefined;
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    captured = JSON.parse(String(init?.body)) as Record<string, unknown>;
    return jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1' });
  });
  const jobCtx = {
    job: { room: { name: 'job-room', sid: 'RM_job' } },
    info: { acceptArguments: { identity: 'job-agent' } },
    room: new FakeJobRoom(),
    addShutdownCallback() {},
  };

  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  await runWithJobContextAsync(jobCtx as never, async () => {
    await av.start(new FakeAgentSession() as never, new FakeJobRoom() as never, {
      livekitUrl: 'wss://example.livekit.cloud',
    });
  });

  expect(captured?.room_name).toBe('job-room');
  expect(captured?.room_sid).toBe('RM_job');
  expect(captured?.agent_identity).toBe('job-agent');
});

it('start falls back to the connected room sid when the job room sid is absent', async () => {
  let captured: Record<string, unknown> | undefined;
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    captured = JSON.parse(String(init?.body)) as Record<string, unknown>;
    return jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1' });
  });
  const room = new FakeConnectedRoom();
  const jobCtx = {
    job: { room: { name: 'job-room' } },
    info: { acceptArguments: { identity: 'job-agent' } },
    room,
    addShutdownCallback() {},
  };

  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  await runWithJobContextAsync(jobCtx as never, async () => {
    await av.start(new FakeAgentSession() as never, room as never, {
      livekitUrl: 'wss://example.livekit.cloud',
    });
  });

  expect(captured?.room_sid).toBe('RM_789');
});

it('start twice raises without creating a second provider session', async () => {
  const fetchMock = vi.fn(async () =>
    jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1' }),
  );
  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  const agentSession = new FakeAgentSession();
  await av.start(agentSession as never, new FakeConnectedRoom() as never, {
    livekitUrl: 'wss://example.livekit.cloud',
  });

  await expect(
    av.start(agentSession as never, new FakeConnectedRoom() as never, {
      livekitUrl: 'wss://example.livekit.cloud',
    }),
  ).rejects.toThrow(/only be called once/);
  expect(fetchMock).toHaveBeenCalledTimes(1);
  expect(av.providerSessionId).toBe('ls_1');
});

it('guards overlapping concurrent start calls (only one provider session)', async () => {
  const fetchMock = vi.fn(async () =>
    jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1' }),
  );
  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  const agentSession = new FakeAgentSession();
  const opts = {
    livekitUrl: 'wss://example.livekit.cloud',
  };

  // Both calls start before either awaits its gateway create; the synchronous guard
  // must let exactly one through so only one billed session is provisioned.
  const results = await Promise.allSettled([
    av.start(agentSession as never, new FakeConnectedRoom() as never, opts),
    av.start(agentSession as never, new FakeConnectedRoom() as never, opts),
  ]);

  expect(results.filter((r) => r.status === 'fulfilled')).toHaveLength(1);
  const rejected = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
  expect(rejected).toHaveLength(1);
  expect(String(rejected[0]?.reason)).toMatch(/only be called once/);
  expect(fetchMock).toHaveBeenCalledTimes(1);
});

it('allows retry after gateway create fails', async () => {
  const idempotencyKeys: string[] = [];
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    idempotencyKeys.push(new Headers(init?.headers).get('Idempotency-Key') ?? '');
    return idempotencyKeys.length === 1
      ? jsonResponse({ error: 'unavailable' }, { status: 503 })
      : jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1' });
  });
  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    connOptions: { maxRetry: 0, retryIntervalMs: 0, timeoutMs: 5 },
  });
  const agentSession = new FakeAgentSession();
  const room = new FakeConnectedRoom();
  const opts = {
    livekitUrl: 'wss://example.livekit.cloud',
  };

  await expect(av.start(agentSession as never, room as never, opts)).rejects.toBeInstanceOf(
    APIStatusError,
  );
  await expect(av.start(agentSession as never, room as never, opts)).resolves.toBeUndefined();

  expect(fetchMock).toHaveBeenCalledTimes(2);
  expect(new Set(idempotencyKeys).size).toBe(1);
  expect(av.providerSessionId).toBe('ls_1');
});

it('retries an ambiguous create failure without removing the avatar participant', async () => {
  const removeParticipant = vi
    .spyOn(RoomServiceClient.prototype, 'removeParticipant')
    .mockResolvedValue(undefined as never);
  const idempotencyKeys: string[] = [];
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    idempotencyKeys.push(new Headers(init?.headers).get('Idempotency-Key') ?? '');
    return idempotencyKeys.length === 1
      ? jsonResponse({ error: 'response lost after create' }, { status: 503 })
      : jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1' });
  });
  const room = new FakeConnectedRoom();
  const jobCtx = {
    job: { room: { name: 'job-room', sid: 'RM_job' } },
    info: {
      url: 'wss://example.livekit.cloud',
      apiKey: 'devkey',
      apiSecret: 'devsecret',
      acceptArguments: { identity: 'job-agent' },
    },
    room,
    addShutdownCallback() {},
  };
  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    connOptions: { maxRetry: 0, retryIntervalMs: 0, timeoutMs: 5 },
  });
  const agentSession = new FakeAgentSession();
  const opts = {
    livekitUrl: 'wss://example.livekit.cloud',
  };

  await runWithJobContextAsync(jobCtx as never, async () => {
    await expect(av.start(agentSession as never, room as never, opts)).rejects.toBeInstanceOf(
      APIStatusError,
    );
    expect(removeParticipant).not.toHaveBeenCalled();

    await expect(av.start(agentSession as never, room as never, opts)).resolves.toBeUndefined();
  });

  expect(new Set(idempotencyKeys).size).toBe(1);
  expect(removeParticipant).not.toHaveBeenCalled();
  expect(av.providerSessionId).toBe('ls_1');
});

it('sets ids before audio rebind failures', async () => {
  const fetchMock = vi.fn(async () =>
    jsonResponse({ session_id: 'AVS_1', provider_session_id: 'ls_1', terminate_token: 'tt_1' }),
  );
  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  const agentSession = new FakeAgentSession();
  Object.defineProperty(agentSession.output, 'audio', {
    set() {
      throw new Error('boom');
    },
  });

  await expect(
    av.start(agentSession as never, new FakeConnectedRoom() as never, {
      livekitUrl: 'wss://example.livekit.cloud',
    }),
  ).rejects.toThrow(/boom/);
  expect(av.providerSessionId).toBe('ls_1');
  expect(av['_terminateToken']).toBe('tt_1');
});

it('start rejects disconnected standalone rooms', async () => {
  const av = makeAvatar();
  await expect(
    av.start(new FakeAgentSession() as never, new FakeRoom() as never, {
      livekitUrl: 'wss://example.livekit.cloud',
    }),
  ).rejects.toThrow(/needs a connected room/);
});

it('aclose terminates session', async () => {
  const terminateBodies: Record<string, unknown>[] = [];
  const fetchMock = vi.fn(async (_url: string | URL | Request, init?: RequestInit) => {
    terminateBodies.push(JSON.parse(String(init?.body)) as Record<string, unknown>);
    return jsonResponse({ terminated: true });
  });
  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  av['_providerSessionId'] = 'ls_abc';
  av['_terminateToken'] = 'tt_abc';

  await av.aclose();

  expect(terminateBodies).toEqual([
    { provider: 'lemonslice', provider_session_id: 'ls_abc', terminate_token: 'tt_abc' },
  ]);
  expect(av.providerSessionId).toBeNull();
  expect(av['_terminateToken']).toBeNull();
});

it('aclose skips terminate without token', async () => {
  const fetchMock = vi.fn(async () => jsonResponse({ terminated: true }));
  const av = makeAvatar({ fetch: fetchMock as typeof fetch });
  av['_providerSessionId'] = 'ls_abc';

  await av.aclose();

  expect(fetchMock).not.toHaveBeenCalled();
});

it('aclose retries terminate then escalates, keeping ids and running base cleanup', async () => {
  const fetchMock = vi.fn(async () => jsonResponse({ error: 'boom' }, { status: 500 }));
  const av = makeAvatar({
    fetch: fetchMock as typeof fetch,
    connOptions: { maxRetry: 2, retryIntervalMs: 0, timeoutMs: 5 },
  });
  av['_providerSessionId'] = 'ls_abc';
  av['_terminateToken'] = 'tt_abc';

  // The failure now propagates (instead of a silent warn) so the job-shutdown handler
  // escalates it.
  await expect(av.aclose()).rejects.toThrow(/keep billing/);
  // Retried with the same policy as create: 1 initial + maxRetry attempts.
  expect(fetchMock).toHaveBeenCalledTimes(3);
  // Ids are retained so a later aclose() can still attempt termination.
  expect(av.providerSessionId).toBe('ls_abc');
  expect(av['_terminateToken']).toBe('tt_abc');
});

it('aclose without session is no-op', async () => {
  await expect(makeAvatar().aclose()).resolves.toBeUndefined();
});
